import React, { useState, useEffect } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from 'lucide-react';
import Drawer from './Drawer';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active: boolean;
  is_active_member: boolean;
  membership_expiry?: string;
  date_joined: string;
  last_login: string | null;
}

interface EditUserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active: boolean;
  is_active_member: boolean;
  membership_expiry?: string;
  password?: string;
  confirm_password?: string;
}

interface EditUserFormProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSubmit: (formData: EditUserFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  errors?: Record<string, string>;
}

const inputCls = (hasError?: boolean) =>
  `w-full px-3 py-2 border ${hasError ? 'border-[var(--lib-danger)]' : 'border-[var(--lib-hairline)]'} rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent`;

const labelCls = 'block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5';

const EditUserForm: React.FC<EditUserFormProps> = ({
  open,
  onClose,
  user,
  onSubmit,
  onCancel,
  isLoading = false,
  errors = {}
}) => {
  const [formData, setFormData] = useState<EditUserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'student',
    student_id: '',
    employee_id: '',
    phone_number: '',
    date_of_birth: '',
    address: '',
    is_active: true,
    is_active_member: true,
    membership_expiry: '',
    password: '',
    confirm_password: '',
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    message: string;
    color: string;
  }>({ score: 0, message: '', color: 'bg-[var(--lib-hairline)]' });

  // Initialize form data with user details
  useEffect(() => {
    if (user) {
      const formatDateForInput = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };
      setFormData({
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        user_type: user.user_type,
        student_id: user.student_id || '',
        employee_id: user.employee_id || '',
        phone_number: user.phone_number || '',
        date_of_birth: formatDateForInput(user.date_of_birth),
        address: user.address || '',
        is_active: user.is_active,
        is_active_member: user.is_active_member,
        membership_expiry: formatDateForInput(user.membership_expiry),
        password: '',
        confirm_password: '',
      });
      setShowPasswordSection(false);
    }
  }, [user]);

  const checkPasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength({ score: 0, message: '', color: 'bg-[var(--lib-hairline)]' });
      return;
    }
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    const map: Record<number, { message: string; color: string }> = {
      0: { message: 'Very Weak', color: 'bg-red-500' },
      1: { message: 'Very Weak', color: 'bg-red-500' },
      2: { message: 'Weak', color: 'bg-orange-500' },
      3: { message: 'Fair', color: 'bg-yellow-500' },
      4: { message: 'Good', color: 'bg-blue-500' },
      5: { message: 'Strong', color: 'bg-green-500' },
    };
    setPasswordStrength({ score, ...map[score] });
  };

  const handleInputChange = (field: keyof EditUserFormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value } as EditUserFormData;
      if (field === 'user_type') {
        if (value === 'student') next.employee_id = '';
        else if (['faculty', 'staff', 'librarian', 'admin'].includes(value)) next.student_id = '';
      }
      return next;
    });
    if (field === 'password') {
      checkPasswordStrength(value);
      if (!value) setShowPasswordSection(false);
    }
  };

  const getFieldError = (field: string) => errors[field] || '';

  const isEmployeeType = ['faculty', 'staff', 'librarian', 'admin'].includes(formData.user_type);
  const isStudent = formData.user_type === 'student';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizeDate = (value?: string | null): string | null | undefined => {
      if (value === undefined) return undefined;
      if (value === null) return null;
      const trimmed = value.trim();
      if (trimmed === '') return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return undefined;
    };

    const submitData: EditUserFormData = {
      username: formData.username,
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      user_type: formData.user_type,
      phone_number: formData.phone_number,
      date_of_birth: normalizeDate(formData.date_of_birth) ?? undefined,
      address: formData.address,
      is_active: formData.is_active,
      is_active_member: formData.is_active_member,
      membership_expiry: normalizeDate(formData.membership_expiry) ?? undefined,
    };

    if (formData.user_type === 'student') {
      submitData.student_id = formData.student_id;
    } else if (['faculty', 'staff', 'librarian', 'admin'].includes(formData.user_type)) {
      submitData.employee_id = formData.employee_id;
    }

    Object.keys(submitData).forEach(k => {
      if (submitData[k as keyof EditUserFormData] === undefined) delete (submitData as any)[k];
    });

    if (formData.password && formData.password.trim()) {
      submitData.password = formData.password;
      submitData.confirm_password = formData.confirm_password;
    }

    await onSubmit(submitData);
  };

  if (!user) return null;

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={isLoading}
        className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="edit-user-form"
        disabled={isLoading}
        className="bg-[var(--lib-gold)] hover:brightness-105 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors"
      >
        {isLoading ? 'Updating...' : 'Update User'}
      </button>
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} title="Edit User" footer={footer} width={520}>
      {/* User Info Header */}
      <div className="mb-5 p-4 bg-[#F8F7F5] border border-[#E8E4DC] rounded-xl flex items-center gap-3">
        <div className="h-10 w-10 bg-[var(--lib-surface-2)] rounded-full flex items-center justify-center text-[var(--lib-gold-text)] font-bold text-sm flex-shrink-0">
          {user.first_name?.[0]}{user.last_name?.[0]}
        </div>
        <div>
          <div className="text-sm font-semibold text-[var(--lib-ink)]">{user.first_name} {user.last_name}</div>
          <div className="text-xs text-[var(--lib-ink-muted)]">@{user.username} · Joined {new Date(user.date_joined).toLocaleDateString()}</div>
          {user.last_login && (
            <div className="text-xs text-[var(--lib-ink-muted)]">Last login: {new Date(user.last_login).toLocaleDateString()}</div>
          )}
        </div>
      </div>

      <form id="edit-user-form" onSubmit={handleSubmit} className="space-y-6">

        {/* Basic Information */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Basic Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>First Name <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.first_name}
                  onChange={e => handleInputChange('first_name', e.target.value)}
                  placeholder="First name"
                  className={inputCls(!!getFieldError('first_name'))}
                />
                {getFieldError('first_name') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('first_name')}
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>Last Name <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.last_name}
                  onChange={e => handleInputChange('last_name', e.target.value)}
                  placeholder="Last name"
                  className={inputCls(!!getFieldError('last_name'))}
                />
                {getFieldError('last_name') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('last_name')}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>Username</label>
              <input
                value={formData.username}
                disabled
                className="w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink-muted)] bg-[var(--lib-surface-2)] cursor-not-allowed"
              />
            </div>
            <div>
              <label className={labelCls}>Email Address <span className="text-[var(--lib-danger)]">*</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder="Email address"
                className={inputCls(!!getFieldError('email'))}
              />
              {getFieldError('email') && (
                <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{getFieldError('email')}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Account Settings */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Account Settings</h3>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>User Type <span className="text-[var(--lib-danger)]">*</span></label>
              <select
                value={formData.user_type}
                onChange={e => handleInputChange('user_type', e.target.value)}
                className={inputCls(!!getFieldError('user_type'))}
              >
                <option value="student">Student</option>
                <option value="faculty">Faculty</option>
                <option value="staff">Staff</option>
                <option value="librarian">Librarian</option>
                <option value="admin">Administrator</option>
                <option value="guest">Guest</option>
              </select>
            </div>
            {isStudent && (
              <div>
                <label className={labelCls}>Student ID <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.student_id || ''}
                  onChange={e => handleInputChange('student_id', e.target.value.toUpperCase())}
                  placeholder="Student ID"
                  className={inputCls(!!getFieldError('student_id'))}
                />
                <p className="text-xs text-[var(--lib-ink-muted)] mt-1">Required for student accounts.</p>
                {getFieldError('student_id') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('student_id')}
                  </p>
                )}
              </div>
            )}
            {isEmployeeType && (
              <div>
                <label className={labelCls}>Employee ID <span className="text-[var(--lib-danger)]">*</span></label>
                <input
                  value={formData.employee_id || ''}
                  onChange={e => handleInputChange('employee_id', e.target.value.toUpperCase())}
                  placeholder="Employee ID"
                  className={inputCls(!!getFieldError('employee_id'))}
                />
                <p className="text-xs text-[var(--lib-ink-muted)] mt-1">Required for faculty, staff, librarian, admin.</p>
                {getFieldError('employee_id') && (
                  <p className="text-xs text-[var(--lib-danger)] mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{getFieldError('employee_id')}
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-col gap-2 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={checked => handleInputChange('is_active', !!checked)}
                />
                <span className="text-sm text-[var(--lib-ink)]">Active Account</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  id="edit-is_active_member"
                  checked={formData.is_active_member}
                  onCheckedChange={checked => handleInputChange('is_active_member', !!checked)}
                />
                <span className="text-sm text-[var(--lib-ink)]">Active Library Member</span>
              </label>
            </div>
          </div>
        </section>

        {/* Personal Information */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">Personal Information</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Phone Number</label>
                <input
                  value={formData.phone_number || ''}
                  onChange={e => handleInputChange('phone_number', e.target.value)}
                  placeholder="Phone"
                  className={inputCls(!!getFieldError('phone_number'))}
                />
              </div>
              <div>
                <label className={labelCls}>Date of Birth</label>
                <input
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={e => handleInputChange('date_of_birth', e.target.value)}
                  className={inputCls(!!getFieldError('date_of_birth'))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Address</label>
                <input
                  value={formData.address || ''}
                  onChange={e => handleInputChange('address', e.target.value)}
                  placeholder="Address"
                  className={inputCls(!!getFieldError('address'))}
                />
              </div>
              <div>
                <label className={labelCls}>Membership Expiry</label>
                <input
                  type="date"
                  value={formData.membership_expiry || ''}
                  onChange={e => handleInputChange('membership_expiry', e.target.value)}
                  className={inputCls(!!getFieldError('membership_expiry'))}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Change Password */}
        <section>
          <h3 className="text-sm font-semibold text-[var(--lib-ink)] mb-3 uppercase tracking-wider font-mono">
            Change Password <span className="normal-case font-normal text-xs text-[var(--lib-ink-muted)]">(optional)</span>
          </h3>
          <p className="text-xs text-[var(--lib-ink-muted)] mb-3">Leave empty to keep the current password.</p>

          {(getFieldError('password') || getFieldError('confirm_password')) && (
            <div className="mb-3 p-3 bg-[var(--lib-surface-2)] border border-[var(--lib-danger)]/30 rounded-lg text-sm text-[var(--lib-danger)] flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {getFieldError('password') || getFieldError('confirm_password')}
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                id="change_password"
                checked={showPasswordSection}
                onCheckedChange={checked => {
                  setShowPasswordSection(!!checked);
                  if (!checked) {
                    handleInputChange('password', '');
                    handleInputChange('confirm_password', '');
                  }
                }}
              />
              <span className="text-sm text-[var(--lib-ink)]">Change password</span>
            </label>

            {(showPasswordSection || formData.password) && (
              <div className="grid grid-cols-1 gap-3 pt-1">
                <div>
                  <label className={labelCls}>New Password</label>
                  <input
                    type="password"
                    value={formData.password || ''}
                    onChange={e => handleInputChange('password', e.target.value)}
                    placeholder="New password"
                    className={inputCls(!!getFieldError('password'))}
                  />
                  {formData.password && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-[var(--lib-ink-muted)]">Strength:</span>
                        <span className={`font-medium ${
                          passwordStrength.score <= 2 ? 'text-[var(--lib-danger)]' :
                          passwordStrength.score <= 3 ? 'text-yellow-600' : 'text-green-600'
                        }`}>{passwordStrength.message}</span>
                      </div>
                      <div className="w-full bg-[var(--lib-surface-2)] rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Confirm New Password</label>
                  <input
                    type="password"
                    value={formData.confirm_password || ''}
                    onChange={e => handleInputChange('confirm_password', e.target.value)}
                    placeholder="Confirm new password"
                    className={inputCls(!!getFieldError('confirm_password'))}
                  />
                  {formData.confirm_password && formData.password && (
                    <p className={`text-xs mt-1 ${formData.password === formData.confirm_password ? 'text-green-600' : 'text-[var(--lib-danger)]'}`}>
                      {formData.password === formData.confirm_password ? '✓ Passwords match' : '✗ Passwords don\'t match'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </form>
    </Drawer>
  );
};

export default EditUserForm;
